import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, MapPin, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

const LEAVE_TYPES = [
  { value: 'sick_leave', label: 'Sick Leave', icon: '🤒', description: 'Working from home due to illness' },
  { value: 'free_period', label: 'Free Period', icon: '📅', description: 'No classes scheduled' },
  { value: 'meeting', label: 'Meeting/Training', icon: '👥', description: 'At an off-campus meeting or training' },
  { value: 'personal', label: 'Personal Leave', icon: '🏠', description: 'Personal matters' },
  { value: 'other', label: 'Other', icon: '📝', description: 'Other reason (please specify)' },
];

export default function LeaveRequestModal({ open, onClose, date, locationStatus }) {
  const [leaveType, setLeaveType] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Check both 'user' and 'currentUser' keys for compatibility
  const user = JSON.parse(localStorage.getItem('user') || localStorage.getItem('currentUser') || '{}');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setLeaveType('');
      setReason('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!leaveType) {
      toast.error('Please select a reason type');
      return;
    }

    if (leaveType === 'other' && !reason.trim()) {
      toast.error('Please provide details for "Other" reason');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/notifications/leave-request?teacher_id=${user.id}&school_id=${user.school_id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            date: date || new Date().toISOString().split('T')[0],
            leave_type: leaveType,
            reason: reason.trim() || null,
          }),
        }
      );

      if (response.ok) {
        toast.success('Reason submitted successfully! Awaiting approval.');
        onClose();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit reason');
      }
    } catch (error) {
      console.error('Error submitting leave request:', error);
      toast.error('Failed to submit reason');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    toast.info('You can submit your reason later from the notifications page');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="leave-request-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Sign-In Location Notice
          </DialogTitle>
          <DialogDescription>
            You signed in from outside the school campus. Please provide a reason for the principal&apos;s records.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Location Status */}
          <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
            <MapPin className="h-4 w-4" />
            <span>
              Location: <strong>{locationStatus === 'off_campus' ? 'Off Campus' : 'Nearby School'}</strong>
            </span>
          </div>

          {/* Leave Type Selection */}
          <div className="space-y-3">
            <Label>Select Reason Type *</Label>
            <RadioGroup value={leaveType} onValueChange={setLeaveType}>
              {LEAVE_TYPES.map((type) => (
                <div
                  key={type.value}
                  className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    leaveType === type.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => setLeaveType(type.value)}
                >
                  <RadioGroupItem value={type.value} id={type.value} className="mt-1" />
                  <div className="flex-1">
                    <label htmlFor={type.value} className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <span>{type.icon}</span>
                      {type.label}
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Additional Details */}
          <div className="space-y-2">
            <Label htmlFor="reason">
              Additional Details {leaveType === 'other' && '*'}
            </Label>
            <Textarea
              id="reason"
              placeholder="Provide any additional information..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="leave-reason-input"
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleSkip} disabled={submitting}>
            Skip for Now
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-leave-btn">
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Reason'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
